// src/components/FacilityDataTable.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import MUIDataTable from "mui-datatables";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteFacility,
  getMyFacilities,
  updateFacility,
  downloadFacilitiesCSV,
  clearFacilityState,
  assignUserToFacility,
  getAssignedUsers,
} from "../redux/slices/facilitySlice";
import { getDashboardUsers } from "../redux/slices/dashboardSlice";
import DateFilter from "./DateFilter";
import CustomerGroupFilter from "./CustomerGroupFilter";
import useDateFilter from "./useDateFilter";
import Toast from "./Toast";

const FacilityDataTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const {
    myFacilities,
    isFetching,
    fetchError,
    isUpdating,
    isDeleting,
    updateError,
    deleteError,
    successMessage,
    downloadCSVError,
    isAssigning,
    assignError,
    assignedUsers,
    isFetchingAssignedUsers,
  } = useSelector((state) => state.facility);

  const { users: dashboardUsers, isLoading: isLoadingUsers } = useSelector(
    (state) => state.dashboard,
  );

  const { dateFilter, updateFilter, getDateRange } = useDateFilter();

  // Local state
  const [customerGroup, setCustomerGroup] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const [editingFacility, setEditingFacility] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const buttonRefs = useRef([]);
  const localTimeDifference = -new Date().getTimezoneOffset() / 60;

  // Assign user modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedFacilityForAssign, setSelectedFacilityForAssign] =
    useState(null);
  const [searchUserQuery, setSearchUserQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [assignedUsersForFacility, setAssignedUsersForFacility] =
    useState(null);
  const [debouncedQuery, setDebouncedQuery] = useState(searchUserQuery);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(searchUserQuery);
    }, 250);

    return () => clearTimeout(t);
  }, [searchUserQuery]);

  // Fetch facilities
  useEffect(() => {
    const dateRange = getDateRange();
    dispatch(
      getMyFacilities({
        page: currentPage,
        from_date: dateRange.from_date,
        to_date: dateRange.to_date,
        customer_group_name: customerGroup || undefined,
        local_time_difference: localTimeDifference,
      }),
    );
  }, [
    dispatch,
    currentPage,
    dateFilter.selectedYear,
    dateFilter.viewType,
    dateFilter.selectedQuarter,
    dateFilter.selectedMonth,
    customerGroup,
  ]);

  // Fetch data when assign modal opens
  useEffect(() => {
    if (showAssignModal && selectedFacilityForAssign) {
      const facilityId =
        selectedFacilityForAssign.facility_id ||
        selectedFacilityForAssign.originalData?.facility_id ||
        selectedFacilityForAssign.id;
      dispatch(getAssignedUsers({ facilityId, page: 1 }));
      dispatch(getDashboardUsers());
    }
  }, [showAssignModal, selectedFacilityForAssign, dispatch]);

  // Store assigned users in local state
  useEffect(() => {
    if (assignedUsers?.data) {
      setAssignedUsersForFacility(assignedUsers);
    }
  }, [assignedUsers]);

  // Reset search and selection when modal closes
  useEffect(() => {
    if (!showAssignModal) {
      setSearchUserQuery("");
      setSelectedUserId("");
    }
  }, [showAssignModal]);

  // Toast notifications
  useEffect(() => {
    const msg =
      successMessage ||
      updateError ||
      deleteError ||
      fetchError ||
      downloadCSVError ||
      assignError;
    if (msg) {
      showToast(msg, successMessage ? "success" : "error");
      dispatch(clearFacilityState());
    }
  }, [
    successMessage,
    updateError,
    deleteError,
    fetchError,
    downloadCSVError,
    assignError,
    dispatch,
  ]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  // Get available users (not already assigned)
  const getAvailableUsers = () => {
    if (!dashboardUsers) return [];
    if (!assignedUsersForFacility?.data) return dashboardUsers;
    const assignedIds = new Set(
      assignedUsersForFacility.data.map((a) => a.user_id),
    );
    return dashboardUsers.filter((user) => !assignedIds.has(user.id));
  };

  // Filter available users by search query
  const filteredUsers = getAvailableUsers().filter(
    (user) =>
      user.name?.toLowerCase().includes(searchUserQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchUserQuery.toLowerCase()) ||
      user.role?.toLowerCase().includes(searchUserQuery.toLowerCase()),
  );

  // Handlers
  const handleDropdownToggle = (index, e) => {
    e.stopPropagation();
    if (buttonRefs.current[index]) {
      const rect = buttonRefs.current[index].getBoundingClientRect();
      setButtonPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX - 120,
      });
    }
    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  const handleEdit = (facility) => {
    setEditingFacility(facility);
    setDropdownOpen(null);
  };

  const handleViewDetails = (facility) => {
    const actualFacilityId =
      facility.facility_id || facility.originalData?.facility_id || facility.id;
    navigate(`/facilities/${actualFacilityId}`, {
      state: { facility: facility.originalData || facility },
    });
    setDropdownOpen(null);
  };

  const handleDelete = (facility) => {
    const actualFacilityId = facility.originalData?.facility_id || facility.id;
    if (window.confirm(`Delete ${facility.facilityName}?`)) {
      dispatch(deleteFacility(actualFacilityId)).then((result) => {
        if (result.payload?.success) {
          const dateRange = getDateRange();
          dispatch(
            getMyFacilities({
              page: currentPage,
              from_date: dateRange.from_date,
              to_date: dateRange.to_date,
              customer_group_name: customerGroup || undefined,
              local_time_difference: localTimeDifference,
            }),
          );
        }
      });
    }
    setDropdownOpen(null);
  };

  const handleAssignUser = (facility) => {
    setSelectedFacilityForAssign(facility);
    setShowAssignModal(true);
    setDropdownOpen(null);
  };

  const handleAssignSubmit = (userId) => {
    if (!userId) {
      showToast("Please select a user", "error");
      return;
    }
    const facilityObj = selectedFacilityForAssign;
    const actualFacilityId =
      facilityObj.facility_id ||
      facilityObj.originalData?.facility_id ||
      facilityObj.id;
    dispatch(
      assignUserToFacility({
        facility_id: actualFacilityId,
        user_id: parseInt(userId),
      }),
    ).then((result) => {
      if (result.payload?.success) {
        showToast(
          `User assigned to ${facilityObj.facilityName} successfully!`,
          "success",
        );
        setShowAssignModal(false);
        setSelectedFacilityForAssign(null);
        setAssignedUsersForFacility(null);
        // Refresh facility list
        const dateRange = getDateRange();
        dispatch(
          getMyFacilities({
            page: currentPage,
            from_date: dateRange.from_date,
            to_date: dateRange.to_date,
            customer_group_name: customerGroup || undefined,
            local_time_difference: localTimeDifference,
          }),
        );
      }
    });
  };

  const handleSaveEdit = (updatedData) => {
    const actualFacilityId =
      editingFacility.facility_id ||
      editingFacility.originalData?.facility_id ||
      editingFacility.id;
    dispatch(
      updateFacility({
        facilityId: actualFacilityId,
        facilityData: {
          name: updatedData.facilityName,
          address: updatedData.address,
          budgeted_hours: updatedData.budgetedHours,
        },
      }),
    ).then((result) => {
      if (result.payload?.success) {
        const dateRange = getDateRange();
        dispatch(
          getMyFacilities({
            page: currentPage,
            from_date: dateRange.from_date,
            to_date: dateRange.to_date,
            customer_group_name: customerGroup || undefined,
            local_time_difference: localTimeDifference,
          }),
        );
        setEditingFacility(null);
      }
    });
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleGroupChange = (group) => {
    setCustomerGroup(group);
    setCurrentPage(1);
  };

  const transformFacilityData = (apiData) =>
    apiData.map((facility) => ({
      id: facility.id,
      facility_id: facility.facility_id,
      facilityName: facility.facility_name || "N/A",
      formsSubmitted: facility.total_assessments || "0",
      totalHoursWorked: facility.total_hours || "0",
      customerName: facility.customer_name || "N/A",
      groupNames: facility.customer_group_name || "N/A",
      budgetedHours: facility.budgeted_hours
        ? Math.round(facility.budgeted_hours)
        : "N/A",
      customerId: facility.customer_id || null,
      address: facility.facility_address || "No address",
      originalData: facility,
    }));

  const facilityData = isFetching
    ? []
    : myFacilities.data?.length
      ? transformFacilityData(myFacilities.data)
      : [];

  const columns = [
    {
      name: "facilityName",
      label: "Facility Name",
      options: { filter: false, sort: false },
    },
    {
      name: "groupNames",
      label: "Group Name",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const rowData = facilityData[tableMeta.rowIndex];
          const customerId = rowData?.customerId;
          return (
            <span
              onClick={() =>
                customerId &&
                navigate(`/customers/${customerId}`, {
                  state: {
                    customerId,
                    customerName: value,
                    from: "facilities",
                  },
                })
              }
              style={{
                color: customerId ? "#8b2885" : "#333",
                cursor: customerId ? "pointer" : "default",
                textDecoration: customerId ? "underline" : "none",
                fontWeight: 500,
              }}>
              {value}
            </span>
          );
        },
      },
    },
    {
      name: "customerName",
      label: "Customer Name",
      options: { filter: false, sort: false },
    },
    {
      name: "formsSubmitted",
      label: "Tasks Submitted",
      options: { filter: false, sort: false },
    },
    {
      name: "budgetedHours",
      label: "Budgeted Hours",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <span style={{ color: "#666", fontSize: "14px" }}>{value}</span>
        ),
      },
    },
    {
      name: "totalHoursWorked",
      label: "Total Hours Worked",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const row = facilityData[tableMeta.rowIndex];
          let displayValue = "N/A",
            color = "#666";
          if (row && value && parseFloat(value) > 0) {
            displayValue = `${value} hours`;
            color = "#29BF5A";
          } else if (row && row.formsSubmitted > 0) {
            displayValue = "In Progress";
            color = "#FF8104";
          } else {
            displayValue = "Not Started";
            color = "#999";
          }
          return <span style={{ color, fontWeight: 500 }}>{displayValue}</span>;
        },
      },
    },
    {
      name: "address",
      label: "Facility Address",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <span style={{ color: "#333", fontWeight: 500 }}>
            {value || "No address"}
          </span>
        ),
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const rowData = facilityData[dataIndex];
          if (!rowData) return null;
          return (
            <div style={{ position: "relative", display: "inline-block" }}>
              <button
                ref={(el) => (buttonRefs.current[dataIndex] = el)}
                onClick={(e) => handleDropdownToggle(dataIndex, e)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px",
                }}>
                <Icon icon="mdi:dots-horizontal" width="25" height="25" />
              </button>
            </div>
          );
        },
      },
    },
  ];

  const options = {
    selectableRows: "none",
    responsive: "standard",
    elevation: 0,
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    filterType: "dropdown",
    search: true,
    searchPlaceholder: "Search facilities...",
    pagination: false,
    serverSide: false,
    count: myFacilities.total || 0,
    rowsPerPage: myFacilities.per_page || 10,
    rowsPerPageOptions: [5, 10, 25, 50],
    page: currentPage - 1,
    onPageChange: (page) => handlePageChange(page + 1),
    onTableChange: (action, tableState) => {
      if (action === "changePage") handlePageChange(tableState.page + 1);
    },
    textLabels: {
      body: { noMatch: isFetching ? "Loading..." : "No facilities found" },
      toolbar: { search: "Search", viewColumns: "View Columns" },
    },
  };

  const dropdownOptions = [
    {
      label: "Edit Facility",
      icon: "line-md:edit",
      color: "#333",
      onClick: handleEdit,
    },
    {
      label: "View Details",
      icon: "mdi:eye-outline",
      color: "#2196F3",
      onClick: handleViewDetails,
    },
    {
      label: "Assign User",
      icon: "mdi:account-plus",
      color: "#8B2885",
      onClick: handleAssignUser,
    },
    {
      label: "Delete",
      icon: "material-symbols:delete-outline",
      color: "#D32F2F",
      onClick: handleDelete,
    },
  ];

  // Modals
  const FacilityEditModal = ({ facility, onClose, onSave }) => {
    const [formData, setFormData] = useState({
      facilityName: facility.facilityName,
      address: facility.address || "",
      budgetedHours: facility.budgetedHours || "",
    });
    const handleChange = (e) =>
      setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(formData);
    };
    return createPortal(
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000,
        }}
        onClick={onClose}>
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "24px",
            width: "90%",
            maxWidth: "500px",
          }}
          onClick={(e) => e.stopPropagation()}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "20px",
            }}>
            <h3 style={{ margin: 0, color: "#8B2885" }}>
              Edit {facility.facilityName}
            </h3>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                fontSize: "20px",
                cursor: "pointer",
              }}>
              ×
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontWeight: "500",
                }}>
                Facility Name
              </label>
              <input
                type="text"
                name="facilityName"
                value={formData.facilityName}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                }}
                required
              />
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontWeight: "500",
                }}>
                Address
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  minHeight: "80px",
                }}
              />
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontWeight: "500",
                }}>
                Budgeted Hours
              </label>
              <input
                type="number"
                name="budgetedHours"
                value={formData.budgetedHours}
                onChange={handleChange}
                step="0.01"
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
              }}>
              <button
                type="button"
                onClick={onClose}
                disabled={isUpdating}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  background: "white",
                  cursor: "pointer",
                }}>
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUpdating}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "6px",
                  background: "#8B2885",
                  color: "white",
                  fontWeight: "500",
                  cursor: "pointer",
                }}>
                {isUpdating ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </div>,
      document.body,
    );
  };

  const AssignUserModal = () => {
    const isLoading = isLoadingUsers || isFetchingAssignedUsers;
    const [searchQuery, setSearchQuery] = useState("");

    // stable available users (memoized)
    const availableUsers = useMemo(() => {
      if (!dashboardUsers) return [];
      if (!assignedUsersForFacility?.data) return dashboardUsers;

      const assignedIds = new Set(
        assignedUsersForFacility.data.map((a) => a.user_id),
      );
      return dashboardUsers.filter((user) => !assignedIds.has(user.id));
    }, [dashboardUsers, assignedUsersForFacility]);

    // simple local filtering
    const filteredUsers = useMemo(() => {
      if (!searchQuery.trim()) return availableUsers;

      const query = searchQuery.toLowerCase();
      return availableUsers.filter(
        (user) =>
          (user.name || "").toLowerCase().includes(query) ||
          (user.email || "").toLowerCase().includes(query) ||
          (user.role || "").toLowerCase().includes(query),
      );
    }, [availableUsers, searchQuery]);

    // Clear search when modal opens
    useEffect(() => {
      if (showAssignModal) {
        setSearchQuery("");
      }
    }, [showAssignModal]);

    const handleSearchChange = (e) => {
      setSearchQuery(e.target.value);
    };

    const handleSelectUser = (userId) => {
      handleAssignSubmit(userId);
    };

    return createPortal(
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000,
        }}
        onClick={() => setShowAssignModal(false)}>
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "24px",
            width: "90%",
            maxWidth: "500px",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={(e) => e.stopPropagation()}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "20px",
            }}>
            <h3 style={{ margin: 0, color: "#8B2885" }}>
              Assign User to {selectedFacilityForAssign?.facilityName}
            </h3>
            <button
              onClick={() => setShowAssignModal(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: "20px",
                cursor: "pointer",
              }}>
              ×
            </button>
          </div>

          {/* Search input - removed onFocus */}
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontWeight: "500",
              }}>
              Search User
            </label>
            <div className="d-flex align-items-center">
              <i className="fas fa-search me-2 text-muted"></i>
              <input
                type="text"
                className="form-control"
                placeholder="Type to search by name, email, or role..."
                value={searchQuery}
                onChange={handleSearchChange}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                }}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* User list */}
          <div
            style={{
              marginBottom: "24px",
              maxHeight: "300px",
              overflowY: "auto",
              border: "1px solid #eee",
              borderRadius: "6px",
            }}>
            {isLoading ? (
              <div style={{ padding: "20px", textAlign: "center" }}>
                Loading users...
              </div>
            ) : filteredUsers.length === 0 ? (
              <div
                style={{
                  padding: "20px",
                  textAlign: "center",
                  color: "#999",
                }}>
                {searchQuery
                  ? "No matching users found"
                  : "No users available to assign"}
              </div>
            ) : (
              filteredUsers.map((user) => (
                <div
                  key={user.id}
                  onMouseDown={() => handleSelectUser(user.id)}
                  style={{
                    padding: "12px 16px",
                    cursor: "pointer",
                    borderBottom: "1px solid #f0f0f0",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f5f5f5")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "white")
                  }>
                  <div style={{ fontWeight: 500 }}>{user.name}</div>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    {user.email} • {user.role}
                  </div>
                </div>
              ))
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
            }}>
            <button
              onClick={() => setShowAssignModal(false)}
              disabled={isAssigning}
              style={{
                padding: "10px 20px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                background: "white",
                cursor: "pointer",
              }}>
              Cancel
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  };
  return (
    <>
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: "", type: "" })}
      />

      <div className="mb-4 pb-2 border-bottom">
        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
          <div style={{ minWidth: "300px" }}>
            <DateFilter
              {...dateFilter}
              onFilterChange={updateFilter}
              size="sm"
            />
          </div>
          <div className="flex-grow-1">
            <CustomerGroupFilter
              selectedGroup={customerGroup}
              onGroupChange={handleGroupChange}
              size="sm"
            />
          </div>
        </div>
        <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
          <span className="badge bg-light text-dark p-2">
            <i className="fas fa-calendar-alt me-1"></i>
            {getDateRange().label}
          </span>
          {customerGroup && (
            <span className="badge bg-info text-white p-2">
              <i className="fas fa-tag me-1"></i>Group: {customerGroup}
            </span>
          )}
          {myFacilities.total > 0 && (
            <span className="badge bg-success p-2">
              <i className="fas fa-building me-1"></i>
              {myFacilities.total} Facilities
            </span>
          )}
        </div>
      </div>

      <div className="basic-data-table">
        <MUIDataTable
          title=""
          data={facilityData}
          columns={columns}
          options={options}
          className="overflow-hidden packageTable"
        />
      </div>

      {dropdownOpen !== null &&
        createPortal(
          <div
            style={{
              position: "absolute",
              top: buttonPosition.top,
              left: buttonPosition.left,
              backgroundColor: "white",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              minWidth: "160px",
              zIndex: 9999,
            }}
            onClick={(e) => e.stopPropagation()}>
            {dropdownOptions.map((option, idx) => (
              <div
                key={idx}
                style={{
                  padding: "10px 16px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "14px",
                  color: option.color,
                  borderTop: idx === 0 ? "none" : "1px solid #F0F0F0",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#F5F5F5")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
                onClick={() => option.onClick(facilityData[dropdownOpen])}>
                <Icon icon={option.icon} width="18" height="18" />
                <span>{option.label}</span>
              </div>
            ))}
          </div>,
          document.body,
        )}

      {editingFacility && (
        <FacilityEditModal
          facility={editingFacility}
          onClose={() => setEditingFacility(null)}
          onSave={handleSaveEdit}
        />
      )}
      {showAssignModal && <AssignUserModal />}
    </>
  );
};

export default FacilityDataTable;
